import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import ReactPlayer from "react-player";
import CustomVideoPlayer from "./CustomVideoPlayer";

// YouTube API types
declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: () => void;
    Vimeo: any;
  }
}

interface ReactVideoPlayerNewProps {
  videoUrl: string;
  title?: string;
  onPlay?: () => void;
  onPause?: (progress?: number) => void;
  onEnded?: () => void;
  onProgress?: (progress: number) => void;
  thumbnail?: string;
  useCredentials?: boolean;
  width?: string | number;
  height?: string | number;
  className?: string;
  style?: React.CSSProperties;
  initialProgress?: number | null; // Percentage (0-100) to resume from
}

// URL detection functions
const isYouTubeUrl = (url: string) => /youtu(\.be|be\.com)/i.test(url);
const isVimeoUrl = (url: string) => /vimeo\.com/i.test(url);
const isDailymotionUrl = (url: string) => /dailymotion\.com/i.test(url);
const isKnownPlatform = (url: string) =>
  isYouTubeUrl(url) || isVimeoUrl(url) || isDailymotionUrl(url);
const isLikelyFileSource = (url: string) =>
  /(\.mp4$|\.m3u8$|\.webm$)/i.test(url);

// Extract video ID from various URL formats
const getYouTubeVideoId = (url: string) => {
  const match = url.match(
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
  );
  return match ? match[1] : null;
};

const getVimeoVideoId = (url: string) => {
  const match = url.match(/vimeo\.com\/(\d+)/);
  return match ? match[1] : null;
};

const getDailymotionVideoId = (url: string) => {
  const match = url.match(/dailymotion\.com\/video\/([^&\n?#]+)/);
  return match ? match[1] : null;
};

const ReactVideoPlayerNew: React.FC<ReactVideoPlayerNewProps> = ({
  videoUrl,
  title,
  onPlay,
  onPause,
  onEnded,
  onProgress,
  thumbnail,
  useCredentials = false,
  width = "100%",
  height = "100%",
  className,
  style,
  initialProgress = null,
}) => {
  // ReactPlayer ref for file sources
  const playerRef = useRef<{
    seekTo: (amount: number | string, type?: "seconds" | "fraction") => void;
    getCurrentTime?: () => number;
    getDuration?: () => number;
  } | null>(null);

  // Custom iframe ref for platform videos
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const lastTimeRef = useRef(0);
  const cleanupRef = useRef<null | (() => void)>(null);
  const isSeekingRef = useRef(false);
  const progressTimerRef = useRef<number | null>(null);
  const youtubePlayerRef = useRef<any>(null);
  const vimeoPlayerRef = useRef<any>(null);
  const controlsHideTimerRef = useRef<number | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [duration, setDuration] = useState(0);
  const [playedSeconds, setPlayedSeconds] = useState(0);
  const [totalTime, setTotalTime] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [volume] = useState(1);

  // Use refs to get current values in callbacks
  const playedSecondsRef = useRef(0);
  const totalTimeRef = useRef(0);

  // Update refs when state changes
  useEffect(() => {
    playedSecondsRef.current = playedSeconds;
  }, [playedSeconds]);

  useEffect(() => {
    totalTimeRef.current = totalTime;
  }, [totalTime]);

  // Determine if URL should be treated as file source
  const treatAsFile = useMemo(() => {
    if (!videoUrl) return false;
    if (isKnownPlatform(videoUrl)) return false;
    return isLikelyFileSource(videoUrl) || ReactPlayer.canPlay(videoUrl);
  }, [videoUrl]);

  // Listen for Vimeo and Dailymotion player events
  useEffect(() => {
    if (treatAsFile || !iframeRef.current) return;

    const handleMessage = (event: MessageEvent) => {
      // Only handle messages from Vimeo or Dailymotion domains
      const origin = event.origin;
      if (!origin.includes("vimeo.com") && !origin.includes("dailymotion.com"))
        return;

      try {
        const data =
          typeof event.data === "string" ? JSON.parse(event.data) : event.data;

        if (data.event === "play") {
          setIsPlaying(true);
        } else if (data.event === "pause") {
          setIsPlaying(false);

          // Calculate progress percentage for Vimeo/Dailymotion via postMessage

          onPause?.(getProgressPercentage());
        } else if (data.event === "ended") {
          setIsPlaying(false);
        } else if (data.event === "volumechange") {
          setIsMuted(data.data.volume === 0);
        } else if (data.event === "timeupdate") {
          // Handle Vimeo time updates for progress tracking
          if (data.data && typeof data.data.seconds === "number") {
            setPlayedSeconds(data.data.seconds);
          }
        } else if (data.event === "loaded") {
          // Handle Vimeo duration when video loads
          if (data.data && typeof data.data.duration === "number") {
            setDuration(data.data.duration);
          }
        } else if (data.event === "ready") {
          // Handle Vimeo ready event
          if (data.data && typeof data.data.duration === "number") {
            setDuration(data.data.duration);
          }
        } else if (data.event === "progress") {
          // Handle Vimeo progress event
          if (data.data && typeof data.data.seconds === "number") {
            setPlayedSeconds(data.data.seconds);
          }
        }
      } catch (e) {
        // Ignore non-JSON messages
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [treatAsFile]);

  // Attach additional DOM protections only when a real <video> element is used (file/HLS)
  useEffect(() => {
    if (!treatAsFile) return;

    const video = document.querySelector("video") as HTMLVideoElement | null;
    if (!video) return;

    video.setAttribute(
      "controlsList",
      "nodownload noplaybackrate noremoteplayback",
    );
    (video as any).disablePictureInPicture = true;
    video.setAttribute("disablePictureInPicture", "true");
    (video as any).disableRemotePlayback = true;
    video.setAttribute("disableRemotePlayback", "true");

    // const handleTimeUpdate = () => {
    //   if (isSeekingRef.current) return;
    //   const delta = video.currentTime - lastTimeRef.current;
    //   if (delta >= 0 && delta <= 1.5) {
    //     lastTimeRef.current = video.currentTime;
    //   }
    // };

    // const handleSeeking = () => {
    //   isSeekingRef.current = true;
    //   const last = lastTimeRef.current || 0;
    //   if (video.currentTime > last + 0.01) {
    //     video.currentTime = last;
    //   } else {
    //     lastTimeRef.current = video.currentTime;
    //   }
    // };

    // const handleSeeked = () => {
    //   const last = lastTimeRef.current || 0;
    //   if (video.currentTime > last + 0.01) {
    //     video.currentTime = last;
    //   } else {
    //     lastTimeRef.current = video.currentTime;
    //   }
    //   isSeekingRef.current = false;
    // };

    // const handleKeyDown = (e: KeyboardEvent) => {
    //   if (e.key === "ArrowRight" || e.key === "End") {
    //     e.preventDefault();
    //     e.stopPropagation();
    //   }
    // };

    // video.addEventListener("timeupdate", handleTimeUpdate);
    // video.addEventListener("seeking", handleSeeking);
    // video.addEventListener("seeked", handleSeeked);
    // video.addEventListener("keydown", handleKeyDown as any);

    // cleanupRef.current = () => {
    //   video.removeEventListener("timeupdate", handleTimeUpdate);
    //   video.removeEventListener("seeking", handleSeeking);
    //   video.removeEventListener("seeked", handleSeeked);
    //   video.removeEventListener("keydown", handleKeyDown as any);
    // };

    return () => {
      if (cleanupRef.current) cleanupRef.current();
    };
  }, [treatAsFile, videoUrl]);

  // Global cleanup on unmount
  useEffect(() => {
    return () => {
      if (cleanupRef.current) cleanupRef.current();
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
        progressTimerRef.current = null;
      }
    };
  }, []);

  const getProgressPercentage = () => {
    const currentTime = playedSecondsRef?.current;
    const duration = totalTimeRef?.current;
    const progressPercentage =
      duration > 0 ? (currentTime / duration) * 100 : 0;
    return progressPercentage;
  };

  const startProgressTimer = useCallback(() => {
    if (progressTimerRef.current != null) return;
    progressTimerRef.current = window.setInterval(() => {
      let currentTime = 0;
      let totalTime = duration;

      if (treatAsFile) {
        // Use ReactPlayer for file sources
        currentTime = playerRef.current?.getCurrentTime?.() || 0;
        totalTime = playerRef.current?.getDuration?.() || duration;
      } else if (youtubePlayerRef.current) {
        // Use YouTube Player API
        try {
          currentTime = youtubePlayerRef.current.getCurrentTime() || 0;
          totalTime = youtubePlayerRef.current.getDuration() || duration;
        } catch (e) {
          return;
        }
      } else if (isVimeoUrl(videoUrl) && vimeoPlayerRef.current) {
        // Use Vimeo Player API for time updates
        try {
          vimeoPlayerRef.current.getCurrentTime().then((time: number) => {
            currentTime = time;
            setPlayedSeconds(time);
          });
          vimeoPlayerRef.current.getDuration().then((dur: number) => {
            totalTime = dur;
            setTotalTime(dur);
            setDuration(dur);
          });
        } catch (e) {
          return;
        }
      } else if (isVimeoUrl(videoUrl)) {
        // For Vimeo, we rely on postMessage events for time updates
        if (totalTime === 0) {
          // Try to get duration from Vimeo iframe
          iframeRef.current?.contentWindow?.postMessage(
            '{"method":"getDuration"}',
            "*",
          );
        }
        return; // Don't update currentTime here, let postMessage handle it
      }

      const last = lastTimeRef.current || 0;
      const delta = currentTime - last;

      if (delta > 1.5) {
        // Large forward jump → block
        if (treatAsFile) {
          playerRef.current?.seekTo(last, "seconds");
        } else if (youtubePlayerRef.current) {
          youtubePlayerRef.current.seekTo(last);
        }
        setPlayedSeconds(last);
      } else {
        // Allow normal forward progression and backward seeks
        setPlayedSeconds(currentTime);
        if (!isSeekingRef.current) {
          if (delta >= 0 && delta <= 1.5) {
            lastTimeRef.current = currentTime;
          } else if (delta < 0) {
            lastTimeRef.current = currentTime;
          }
        }
      }

      if (totalTime && totalTime !== duration) {
        setDuration(totalTime);
      }
      setTotalTime(totalTime);

      // Call onProgress callback with percentage
      if (onProgress && totalTime > 0 && currentTime > 0) {
        const progressPercentage = (currentTime / totalTime) * 100;
        onProgress(progressPercentage);
      }
    }, 250);
  }, [treatAsFile, videoUrl, duration, onProgress]);

  const stopProgressTimer = () => {
    if (progressTimerRef.current != null) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
  };

  // Ensure progress ticker runs whenever a source URL is present
  useEffect(() => {
    if (videoUrl) {
      startProgressTimer();
    }
    return () => {
      stopProgressTimer();
    };
  }, [videoUrl]);

  // Auto-hide custom controls when not interacting (only for iframe/platform videos)
  useEffect(() => {
    if (treatAsFile) return;

    const handleFsChange = () => {
      // On entering fullscreen, show controls briefly
      setShowControls(true);
      if (controlsHideTimerRef.current) {
        clearTimeout(controlsHideTimerRef.current);
      }
      controlsHideTimerRef.current = window.setTimeout(() => {
        setShowControls(false);
      }, 2000);
    };

    document.addEventListener("fullscreenchange", handleFsChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFsChange);
      if (controlsHideTimerRef.current) {
        clearTimeout(controlsHideTimerRef.current);
        controlsHideTimerRef.current = null;
      }
    };
  }, [treatAsFile]);

  const handleMouseMove = () => {
    if (treatAsFile) return;
    setShowControls(true);
    if (controlsHideTimerRef.current) {
      clearTimeout(controlsHideTimerRef.current);
    }
    controlsHideTimerRef.current = window.setTimeout(() => {
      setShowControls(false);
    }, 2000);
  };

  // YouTube Player API integration
  useEffect(() => {
    if (!isYouTubeUrl(videoUrl) || !iframeRef.current) return;

    // Load YouTube Player API
    const loadYouTubeAPI = () => {
      if (window.YT && window.YT.Player) {
        initializeYouTubePlayer();
      } else {
        const script = document.createElement("script");
        script.src = "https://www.youtube.com/iframe_api";
        script.async = true;
        document.head.appendChild(script);

        window.onYouTubeIframeAPIReady = () => {
          initializeYouTubePlayer();
        };
      }
    };

    const initializeYouTubePlayer = () => {
      const videoId = getYouTubeVideoId(videoUrl);
      if (!videoId || !iframeRef.current) return;

      youtubePlayerRef.current = new window.YT.Player(iframeRef.current, {
        videoId: videoId,
        playerVars: {
          controls: 0,
          disablekb: 1,
          modestbranding: 1,
          rel: 0,
          fs: 1,
          iv_load_policy: 3,
          origin: window.location.origin,
        },
        events: {
          onReady: (event: any) => {
            const duration = event.target.getDuration();
            setDuration(duration);
            setTotalTime(duration);
            // Seek to initial progress if provided
            if (
              initialProgress !== null &&
              initialProgress !== undefined &&
              initialProgress > 0 &&
              duration > 0
            ) {
              const seekTime = (initialProgress / 100) * duration;
              // Seek if progress is greater than 0.5 seconds (to avoid very small seeks)
              if (seekTime >= 0.5 && seekTime < duration) {
                event.target.seekTo(seekTime, true);
                setPlayedSeconds(seekTime);
                lastTimeRef.current = seekTime;
              }
            }
          },
          onStateChange: (event: any) => {
            const state = event.data;
            if (state === window.YT.PlayerState.PLAYING) {
              setIsPlaying(true);
              onPlay?.();
            } else if (state === window.YT.PlayerState.PAUSED) {
              setIsPlaying(false);
              onPause?.(getProgressPercentage());
            } else if (state === window.YT.PlayerState.ENDED) {
              setIsPlaying(false);
              onEnded?.();
            }
          },
        },
      });
    };

    loadYouTubeAPI();

    return () => {
      if (youtubePlayerRef.current) {
        youtubePlayerRef.current.destroy();
        youtubePlayerRef.current = null;
      }
    };
  }, [videoUrl]);

  // Load Vimeo API
  useEffect(() => {
    if (!isVimeoUrl(videoUrl) || !iframeRef.current) return;

    const loadVimeoAPI = () => {
      if (window.Vimeo && window.Vimeo.Player) {
        initializeVimeoPlayer();
        return;
      }

      const script = document.createElement("script");
      script.src = "https://player.vimeo.com/api/player.js";
      script.async = true;
      script.onload = () => {
        initializeVimeoPlayer();
      };
      document.head.appendChild(script);
    };

    const initializeVimeoPlayer = () => {
      const videoId = getVimeoVideoId(videoUrl);
      if (!videoId || !iframeRef.current) return;

      try {
        vimeoPlayerRef.current = new window.Vimeo.Player(iframeRef.current, {
          id: videoId,
          width: "100%",
          height: "100%",
        });

        vimeoPlayerRef.current.ready().then(() => {
          vimeoPlayerRef.current.getDuration().then((duration: number) => {
            setDuration(duration);
            setTotalTime(duration);
            // Seek to initial progress if provided
            if (
              initialProgress !== null &&
              initialProgress !== undefined &&
              duration > 0
            ) {
              const seekTime = (initialProgress / 100) * duration;
              if (seekTime > 0 && seekTime < duration) {
                vimeoPlayerRef.current.setCurrentTime(seekTime);
                setPlayedSeconds(seekTime);
                lastTimeRef.current = seekTime;
              }
            }
          });
        });

        vimeoPlayerRef.current.on("play", () => {
          setIsPlaying(true);
        });

        vimeoPlayerRef.current.on("pause", () => {
          setIsPlaying(false);

          // Calculate progress percentage for Vimeo
          const currentTime = playedSecondsRef?.current;
          const duration = totalTimeRef?.current;
          const progressPercentage =
            duration > 0 ? (currentTime / duration) * 100 : 0;
          onPause?.(progressPercentage);
        });

        vimeoPlayerRef.current.on("ended", () => {
          setIsPlaying(false);
        });
      } catch (error) {
        console.error("Error creating Vimeo player:", error);
      }
    };

    loadVimeoAPI();

    return () => {
      if (vimeoPlayerRef.current) {
        vimeoPlayerRef.current.destroy();
        vimeoPlayerRef.current = null;
      }
    };
  }, [videoUrl, initialProgress]);

  // Handle initialProgress changes for YouTube player after it's ready
  useEffect(() => {
    if (
      youtubePlayerRef.current &&
      initialProgress !== null &&
      initialProgress !== undefined &&
      initialProgress > 0 &&
      duration > 0
    ) {
      try {
        const seekTime = (initialProgress / 100) * duration;
        // Seek if progress is greater than 0.5 seconds (to avoid very small seeks)
        if (seekTime >= 0.5 && seekTime < duration) {
          youtubePlayerRef.current.seekTo(seekTime, true);
          setPlayedSeconds(seekTime);
          lastTimeRef.current = seekTime;
        }
      } catch (e) {
        // Player might not be ready yet
      }
    }
  }, [initialProgress, duration, videoUrl]);

  // Handle initialProgress changes for Vimeo player after it's ready
  useEffect(() => {
    if (
      vimeoPlayerRef.current &&
      initialProgress !== null &&
      initialProgress !== undefined &&
      initialProgress > 0 &&
      duration > 0
    ) {
      const seekTime = (initialProgress / 100) * duration;
      // Seek if progress is greater than 0.5 seconds (to avoid very small seeks)
      if (seekTime >= 0.5 && seekTime < duration) {
        vimeoPlayerRef.current.setCurrentTime(seekTime).catch(() => {
          // Player might not be ready yet
        });
        setPlayedSeconds(seekTime);
        lastTimeRef.current = seekTime;
      }
    }
  }, [initialProgress, duration, videoUrl]);

  const handleProgress = (_progressState: any) => {
    // For file sources, use ReactPlayer progress
    if (treatAsFile) {
      const currentTime = playerRef.current?.getCurrentTime?.() || 0;
      const last = lastTimeRef.current || 0;
      const delta = currentTime - last;

      if (delta > 1.5) {
        playerRef.current?.seekTo(last, "seconds");
        setPlayedSeconds(last);
        return;
      }

      setPlayedSeconds(currentTime);
      if (!isSeekingRef.current) {
        if (delta >= 0 && delta <= 1.5) {
          lastTimeRef.current = currentTime;
        } else if (delta < 0) {
          lastTimeRef.current = currentTime;
        }
      }
    }
  };

  const handleSeek = (seconds: number) => {
    isSeekingRef.current = true;
    const last = lastTimeRef.current || 0;
    if ((seconds || 0) > last + 0.01) {
      if (treatAsFile) {
        playerRef.current?.seekTo(last, "seconds");
      } else if (youtubePlayerRef.current) {
        youtubePlayerRef.current.seekTo(last);
      }
    } else {
      lastTimeRef.current = seconds || 0;
    }
    isSeekingRef.current = false;
  };

  const togglePlay = () => {
    if (treatAsFile) {
      setIsPlaying((p) => !p);
    } else if (youtubePlayerRef.current) {
      if (isPlaying) {
        youtubePlayerRef.current.pauseVideo();
        setIsPlaying(false);
      } else {
        youtubePlayerRef.current.playVideo();
        setIsPlaying(true);
      }
    } else if (iframeRef.current) {
      // For Vimeo and Dailymotion, use postMessage API
      const iframe = iframeRef.current;
      if (isVimeoUrl(videoUrl) && vimeoPlayerRef.current) {
        if (isPlaying) {
          vimeoPlayerRef.current.pause();
          setIsPlaying(false);
        } else {
          vimeoPlayerRef.current.play();
          setIsPlaying(true);
        }
      } else if (isVimeoUrl(videoUrl)) {
        if (isPlaying) {
          iframe.contentWindow?.postMessage('{"method":"pause"}', "*");
          setIsPlaying(false);
        } else {
          iframe.contentWindow?.postMessage('{"method":"play"}', "*");
          setIsPlaying(true);
        }
      } else if (isDailymotionUrl(videoUrl)) {
        if (isPlaying) {
          iframe.contentWindow?.postMessage('{"method":"pause"}', "*");
          setIsPlaying(false);
        } else {
          iframe.contentWindow?.postMessage('{"method":"play"}', "*");
          setIsPlaying(true);
        }
      }
    }
  };

  const toggleMute = () => {
    if (treatAsFile) {
      setIsMuted((m) => !m);
    } else if (youtubePlayerRef.current) {
      if (isMuted) {
        youtubePlayerRef.current.unMute();
        setIsMuted(false);
      } else {
        youtubePlayerRef.current.mute();
        setIsMuted(true);
      }
    } else if (iframeRef.current) {
      // For Vimeo and Dailymotion, use postMessage API
      const iframe = iframeRef.current;
      if (isVimeoUrl(videoUrl) && vimeoPlayerRef.current) {
        if (isMuted) {
          vimeoPlayerRef.current.setVolume(1);
          setIsMuted(false);
        } else {
          vimeoPlayerRef.current.setVolume(0);
          setIsMuted(true);
        }
      } else if (isVimeoUrl(videoUrl)) {
        if (isMuted) {
          iframe.contentWindow?.postMessage(
            '{"method":"setVolume","value":1}',
            "*",
          );
          setIsMuted(false);
        } else {
          iframe.contentWindow?.postMessage(
            '{"method":"setVolume","value":0}',
            "*",
          );
          setIsMuted(true);
        }
      } else if (isDailymotionUrl(videoUrl)) {
        if (isMuted) {
          iframe.contentWindow?.postMessage(
            '{"method":"setVolume","value":1}',
            "*",
          );
          setIsMuted(false);
        } else {
          iframe.contentWindow?.postMessage(
            '{"method":"setVolume","value":0}',
            "*",
          );
          setIsMuted(true);
        }
      }
    }
  };

  const toggleFullscreen = () => {
    if (treatAsFile) {
      // For file sources, use native fullscreen
      const video = document.querySelector("video") as HTMLVideoElement | null;
      if (video) {
        if (video.requestFullscreen) {
          video.requestFullscreen();
        } else if ((video as any).webkitRequestFullscreen) {
          (video as any).webkitRequestFullscreen();
        } else if ((video as any).msRequestFullscreen) {
          (video as any).msRequestFullscreen();
        }
      }
    } else if (iframeRef.current) {
      // For platform iframes (YouTube, Vimeo, Dailymotion), use the iframe Fullscreen API
      if (iframeRef.current.requestFullscreen) {
        iframeRef.current.requestFullscreen();
      } else if ((iframeRef.current as any).webkitRequestFullscreen) {
        (iframeRef.current as any).webkitRequestFullscreen();
      } else if ((iframeRef.current as any).msRequestFullscreen) {
        (iframeRef.current as any).msRequestFullscreen();
      }
    }
  };

  const formatTime = (secs: number) => {
    if (!isFinite(secs)) return "0:00";
    const s = Math.floor(secs % 60)
      .toString()
      .padStart(2, "0");
    const m = Math.floor((secs / 60) % 60)
      .toString()
      .padStart(2, "0");
    const h = Math.floor(secs / 3600);
    return h > 0 ? `${h}:${m}:${s}` : `${Number(m)}:${s}`;
  };

  // Generate iframe URL for different platforms
  const getIframeUrl = () => {
    if (isYouTubeUrl(videoUrl)) {
      const videoId = getYouTubeVideoId(videoUrl);
      return `https://www.youtube-nocookie.com/embed/${videoId}?enablejsapi=1&controls=0&disablekb=1&modestbranding=1&rel=0&fs=1&iv_load_policy=3&origin=${window.location.origin}`;
    } else if (isVimeoUrl(videoUrl)) {
      const videoId = getVimeoVideoId(videoUrl);
      return `https://player.vimeo.com/video/${videoId}?autoplay=0&controls=0&keyboard=0&title=0&byline=0&portrait=0&background=0&transparent=0&autopause=0&loop=0&muted=0&quality=auto&responsive=1&dnt=1&api=1&autopause=0&background=0&byline=0&color=ffffff&portrait=0&title=0&transparent=0`;
    } else if (isDailymotionUrl(videoUrl)) {
      const videoId = getDailymotionVideoId(videoUrl);
      return `https://www.dailymotion.com/embed/video/${videoId}?autoplay=0&controls=0&keyboard=0&ui-start-screen-info=0&ui-logo=0&ui-highlight=0&ui-theme=dark&ui-related=0&sharing-enable=0&queue-enable=0&syndication=0&chromeless=0&mute=0&t=0&start=0&endscreen-enable=0&ui-mini=0&ui-fs=1&api=1`;
    }
    return videoUrl;
  };

  const handlePlay = () => {
    setIsPlaying(true);
    onPlay?.();
  };

  const handlePause = () => {
    setIsPlaying(false);
    onPause?.();
  };

  const handleEnded = () => {
    setIsPlaying(false);
    onEnded?.();
  };

  return (
    <div
      ref={wrapperRef}
      className={className}
      style={{
        position: "relative",
        width,
        height,
        backgroundColor: "#000",
        ...style,
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onKeyDown={(e) => {
        if (e.key === "ArrowRight" || e.key === "End") {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
      onDoubleClick={(e) => {
        // Block double-click to fullscreen on iframes
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <style>{`
        /* Hide overflow button on native controls if they ever appear */
        video::-webkit-media-controls-overflow-button{display:none!important;}
      `}</style>

      {/* Player */}
      <div
        style={{
          position: "relative",
          paddingTop: treatAsFile ? "0px" : "56.25%",
        }}
        onMouseMove={handleMouseMove}
      >
        {treatAsFile ? (
          // ReactPlayer for file sources (MP4, HLS, etc.)
          <>
            <style>{`
              video::-webkit-media-controls-overflow-button{display:none!important;}
            `}</style>
            {/* <ReactPlayer
              ref={playerRef}
              src={videoUrl}
              controls
              width="100%"
              height="100%"
              crossOrigin={useCredentials ? "use-credentials" : undefined}
              onPlay={onPlay}
              onPause={onPause}
              onEnded={onEnded}
              light={thumbnail || undefined}
              onReady={() => {
                // Attach listeners once the real <video> is in the DOM
                const video = document.querySelector(
                  "video"
                ) as HTMLVideoElement | null;
                if (!video) return;

                if (useCredentials) {
                  try {
                    video.setAttribute("crossOrigin", "use-credentials");
                  } catch (e) {}
                }

                // Track last permitted time and prevent forward seeking
                const handleTimeUpdate = () => {
                  if (isSeekingRef.current) return;
                  const delta = video.currentTime - lastTimeRef.current;
                  if (delta >= 0 && delta <= 1.5) {
                    lastTimeRef.current = video.currentTime;
                  }
                };

                const handleSeeking = () => {
                  isSeekingRef.current = true;
                  const last = lastTimeRef.current || 0;
                  // Allow only backward seeks (current <= last). Any forward jump reverts.
                  if (video.currentTime > last + 0.01) {
                    video.currentTime = last;
                  } else {
                    lastTimeRef.current = video.currentTime;
                  }
                };

                // Catch click-to-seek completions
                const handleSeeked = () => {
                  const last = lastTimeRef.current || 0;
                  if (video.currentTime > last + 0.01) {
                    video.currentTime = last;
                  } else {
                    // Backwards seek completed; accept new time as the latest allowed
                    lastTimeRef.current = video.currentTime;
                  }
                  isSeekingRef.current = false;
                };

                const handleKeyDown = (e: KeyboardEvent) => {
                  // Allow ArrowLeft/Home (backwards). Block ArrowRight/End (forward)
                  if (e.key === "ArrowRight" || e.key === "End") {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                };

                video.addEventListener("timeupdate", handleTimeUpdate);
                video.addEventListener("seeking", handleSeeking);
                video.addEventListener("seeked", handleSeeked);
                video.addEventListener("keydown", handleKeyDown as any);

                cleanupRef.current = () => {
                  video.removeEventListener("timeupdate", handleTimeUpdate);
                  video.removeEventListener("seeking", handleSeeking);
                  video.removeEventListener("seeked", handleSeeked);
                  video.removeEventListener("keydown", handleKeyDown as any);
                };
              }}
            /> */}
            <CustomVideoPlayer
              url={videoUrl}
              onPlay={onPlay}
              onPause={onPause}
              onEnded={onEnded}
              onProgress={onProgress}
              initialProgress={initialProgress}
            />
          </>
        ) : (
          // Custom iframe for platform videos (YouTube, Vimeo, Dailymotion)
          <iframe
            ref={iframeRef}
            src={getIframeUrl()}
            width="100%"
            height="100%"
            style={{ position: "absolute", top: 0, left: 0, border: "none" }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            allowFullScreen
            title="Video Player"
            onLoad={() => {
              startProgressTimer();
            }}
          />
        )}
        {!treatAsFile && (
          <>
            {/* Interaction guard overlay (blocks native/iframe interactions) */}
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 60,
                cursor: isPlaying ? "default" : "pointer",
                zIndex: 2,
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            />

            {/* Minimal custom controls */}
            <div
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "12px 16px",
                background:
                  "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 100%)",
                color: "#fff",
                userSelect: "none",
                zIndex: 3,
                flexWrap: "wrap",
                opacity: showControls ? 1 : 0,
                pointerEvents: showControls ? "auto" : "none",
                transition: "opacity 200ms ease",
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  togglePlay();
                }}
                style={{
                  background: "none",
                  border: "none",
                  color: "#fff",
                  fontSize: "24px",
                  cursor: "pointer",
                  padding: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "4px",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor =
                    "rgba(255, 255, 255, 0.1)";
                  e.currentTarget.style.transform = "scale(1.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.transform = "scale(1)";
                }}
                title={isPlaying ? "Pause" : "Play"}
              >
                {isPlaying ? "⏸" : "▶"}
              </button>

              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleMute();
                }}
                style={{
                  background: "none",
                  border: "none",
                  color: "white",
                  fontSize: "20px",
                  cursor: "pointer",
                  padding: "4px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "4px",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor =
                    "rgba(255, 255, 255, 0.1)";
                  e.currentTarget.style.transform = "scale(1.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.transform = "scale(1)";
                }}
                title={isMuted ? "Unmute" : "Mute"}
              >
                {isMuted ? "🔇" : "🔊"}
              </button>

              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleFullscreen();
                }}
                style={{
                  background: "none",
                  border: "none",
                  color: "white",
                  fontSize: "20px",
                  cursor: "pointer",
                  padding: "4px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "4px",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor =
                    "rgba(255, 255, 255, 0.1)";
                  e.currentTarget.style.transform = "scale(1.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.transform = "scale(1)";
                }}
                title="Fullscreen"
              >
                ⛶
              </button>

              {/* Progress bar */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flex: 1,
                }}
              >
                <span
                  style={{ fontVariantNumeric: "tabular-nums", minWidth: 44 }}
                >
                  {formatTime(playedSeconds)}
                </span>
                <input
                  type="range"
                  min={0}
                  max={Math.max(duration, lastTimeRef.current || 0) || 0}
                  step={0.1}
                  value={Math.min(
                    playedSeconds,
                    Math.max(duration, lastTimeRef.current || 0),
                  )}
                  onChange={(e) => {
                    const raw = Number(e.target.value || 0);
                    const safe = Math.min(raw, lastTimeRef.current || 0);
                    setPlayedSeconds(safe);
                    if (treatAsFile) {
                      playerRef.current?.seekTo(safe, "seconds");
                    } else if (youtubePlayerRef.current) {
                      youtubePlayerRef.current.seekTo(safe);
                    }
                  }}
                  onMouseDown={() => {
                    isSeekingRef.current = true;
                  }}
                  onMouseUp={() => {
                    isSeekingRef.current = false;
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowRight" || e.key === "End") {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                  style={{ flex: 1 }}
                />
                <span
                  style={{
                    fontVariantNumeric: "tabular-nums",
                    minWidth: 44,
                    textAlign: "right",
                  }}
                >
                  {formatTime(duration || 0)}
                </span>
              </div>

              <div
                style={{ marginLeft: "auto", opacity: 0.85, fontSize: 12 }}
              ></div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ReactVideoPlayerNew;
